<template lang="pug">
  .monthly-reader(role="dialog" aria-modal="true" aria-label="月刊閱讀器" @dblclick.prevent)
    button.monthly-close(ref="close" type="button" aria-label="關閉月刊" @click="closeReader") ×
    .monthly-message(v-if="!isReady") 月刊載入中…
    .monthly-message(v-else-if="error" role="alert")
      p {{ error }}
      button(type="button" @click="load") 重新載入
    template(v-else)
      template(v-if="lightweight")
        .monthly-pages(key="mobile-pages" ref="pages" :class="{'is-dragging': mouseDrag}" @scroll.passive="onScroll" @pointerdown="startMouseDrag" @pointermove="moveMouseDrag" @pointerup="endMouseDrag" @pointercancel="cancelMouseDrag" @lostpointercapture="cancelMouseDrag")
          .monthly-page(v-for="(page, index) in pages" :key="page.id || index")
            .monthly-image-scroll
              img(v-if="Math.abs(index - currentPage) <= 1" :src="page.src" :alt="`第 ${index + 1} 頁`" decoding="async" :draggable="false" :style="pageStyle(page, index)" @load="imageLoaded(page, $event)" @error="imageFailed")
        ReaderToolbar(key="mobile-controls" :pages="pages" :current-page="currentPage" :zoom="zoom" :book-id="$route.params.id" :title="bookTitle" :reader-element="readerElement" @go="go" @zoom="zoom = $event")
      #book-container.monthly-book(v-else key="desktop-book")
</template>

<script>
import { updateEbooksBrowse } from '@/api/sound.js'
import ReaderToolbar from '@/components/monthly/ReaderToolbar.vue'

const proxy = url => String(url || '').replace('/storage/ebooks/', '/proxy/ebook/')
export default {
  name: 'MonthlyDetail',
  components: { ReaderToolbar },
  data () {
    return { pageRatios: {}, readingWidth: 0, readingHeight: 0, mouseDrag: null, bookTitle: '', isReady: false, error: '', images: [], pages: [], currentPage: 0, zoom: 1, lightweight: true, forceLightweight: false, readerMode: null, flipbook: null, requestId: 0, oldOverflow: '', resizeTimer: null, destroyed: false, renderId: 0 }
  },
  mounted () {
    this.returnFocus = document.activeElement
    this.$refs.close.focus()
    this.readerKeys = event => {
      if (event.key === 'Escape' && !(document.fullscreenElement || document.webkitFullscreenElement)) this.closeReader()
      if (event.key !== 'Tab') return
      const nodes = Array.from(this.$el.querySelectorAll('button:not([disabled]), input, a[href], [tabindex="0"]')).filter(node => node.getClientRects().length)
      const first = nodes[0]; const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', this.readerKeys)
    this.oldOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    this.contextLost = event => {
      if (!this.$el.contains(event.target)) return
      event.preventDefault()
      this.forceLightweight = true
      this.setupReader()
    }
    this.resizeReader = () => {
      this.measureReader()
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        // Fullscreen can enlarge a phone viewport; keep the active reader and controls.
        if (document.fullscreenElement || document.webkitFullscreenElement) return
        const mobile = this.forceLightweight || window.innerWidth < 768
        if (mobile !== this.readerMode) this.setupReader()
      }, 200)
    }
    this.fullscreenChanged = () => { this.zoom = 1; this.$nextTick(() => this.measureReader()) }
    document.addEventListener('fullscreenchange', this.fullscreenChanged)
    document.addEventListener('webkitfullscreenchange', this.fullscreenChanged)
    window.addEventListener('resize', this.resizeReader)
    window.addEventListener('webglcontextlost', this.contextLost, true)
    this.load()
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.readerKeys)
    if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true })
    this.mouseDrag = null
    this.destroyed = true
    this.requestId++
    this.renderId++
    clearTimeout(this.resizeTimer)
    document.removeEventListener('fullscreenchange', this.fullscreenChanged)
    document.removeEventListener('webkitfullscreenchange', this.fullscreenChanged)
    window.removeEventListener('resize', this.resizeReader)
    window.removeEventListener('webglcontextlost', this.contextLost, true)
    this.destroyBook()
    this.pages = []
    document.documentElement.style.overflow = this.oldOverflow
  },
  watch: {
    currentPage () { this.resetPageScroll() },
    zoom (value) { if (value === 1) this.resetPageScroll() }
  },
  methods: {
    closeReader () { this.$router.push('/sound/monthly') },
    measureReader () {
      const element = this.$refs.pages
      if (!element) return
      const width = element.clientWidth
      const resized = width !== this.readingWidth
      this.readingWidth = width
      this.readingHeight = element.clientHeight
      // Image loads must not interrupt an in-progress page swipe.
      if (resized) element.scrollLeft = width * this.currentPage
    },
    imageLoaded (page, event) {
      const image = event.target
      if (image.naturalHeight) this.$set(this.pageRatios, page.id, image.naturalWidth / image.naturalHeight)
      this.measureReader()
    },
    pageStyle (page, index) {
      const ratio = this.pageRatios[page.id]
      if (!ratio || !this.readingWidth || !this.readingHeight) return { width: '100%', height: '100%', objectFit: 'contain' }
      const fit = Math.min(this.readingWidth, this.readingHeight * ratio)
      return { width: `${fit * (index === this.currentPage ? this.zoom : 1)}px`, height: 'auto', maxWidth: 'none' }
    },
    startMouseDrag (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return
      const element = this.$refs.pages
      const scroll = this.zoom > 1 ? event.target.closest('.monthly-image-scroll') : element
      if (!scroll) return
      event.preventDefault()
      this.mouseDrag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: scroll.scrollLeft, top: scroll.scrollTop, page: this.currentPage, pan: this.zoom > 1, turned: false, scroll }
      element.setPointerCapture(event.pointerId)
    },
    moveMouseDrag (event) {
      const drag = this.mouseDrag
      if (!drag || drag.id !== event.pointerId) return
      event.preventDefault()
      if (drag.pan) {
        drag.scroll.scrollLeft = drag.left + drag.x - event.clientX
        drag.scroll.scrollTop = drag.top + drag.y - event.clientY
        return
      }
      if (drag.turned) return
      const distance = drag.x - event.clientX
      if (Math.abs(distance) < 3 || Math.abs(distance) <= Math.abs(drag.y - event.clientY)) return
      drag.turned = true
      const page = Math.max(0, Math.min(this.pages.length - 1, drag.page + Math.sign(distance)))
      this.go(page)
      this.currentPage = page
    },
    finishMouseDrag (event, cancelled) {
      const drag = this.mouseDrag
      if (!drag || drag.id !== event.pointerId) return
      const element = this.$refs.pages
      this.mouseDrag = null
      if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId)
      if (drag.pan || drag.turned) return
      const distance = drag.x - event.clientX
      const step = !cancelled && Math.abs(distance) >= 3 && Math.abs(distance) > Math.abs(drag.y - event.clientY) ? Math.sign(distance) : 0
      this.$nextTick(() => { if (!this.destroyed && this.$refs.pages) this.go(drag.page + step) })
    },
    endMouseDrag (event) { this.finishMouseDrag(event, false) },
    cancelMouseDrag (event) { this.finishMouseDrag(event, true) },
    readerElement () { return this.$el },
    resetPageScroll () {
      this.$nextTick(() => {
        if (this.destroyed || !this.$refs.pages) return
        const page = this.$refs.pages.children[this.currentPage]
        const scroll = page && page.firstElementChild
        if (scroll) { scroll.scrollTop = 0; scroll.scrollLeft = 0 }
      })
    },
    destroyBook () {
      const instance = this.flipbook
      this.flipbook = null
      // FlipBook.destroy releases resources but leaves its wrapper in the DOM.
      const wrapper = instance && instance.wrapper
      if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper)
      this.pendingDestroy = Promise.resolve(this.pendingDestroy).then(() => {
        if (instance && typeof instance.destroy === 'function') return instance.destroy()
      }).catch(() => {})
      return this.pendingDestroy
    },
    async load () {
      const requestId = ++this.requestId
      this.isReady = false; this.error = ''; this.readerMode = null; this.destroyBook()
      try {
        const item = await this.$store.dispatch('getModelById', { name: 'Ebooks', id: Number(this.$route.params.id) })
        if (this.destroyed || requestId !== this.requestId) return
        this.images = (item.ebook_images || []).filter(page => page && page.image)
        if (!this.images.length) throw new Error('此月刊尚無可閱讀的內容')
        this.bookTitle = item.title || '東震月刊'
        const requestedPage = Number(this.$route.query.page)
        this.currentPage = Number.isInteger(requestedPage) && requestedPage > 0 ? Math.min(this.images.length - 1, requestedPage - 1) : 0
        this.isReady = true
        await this.setupReader()
        updateEbooksBrowse(this.$route.params.id, { view_from: window.innerWidth < 768 ? 'mobile' : 'desktop' }).catch(() => {})
      } catch (e) {
        if (this.destroyed || requestId !== this.requestId) return
        this.error = '月刊尚未發布、已隱藏或載入失敗，請稍後重試。'
        this.isReady = true
      }
    },
    async setupReader () {
      if (this.destroyed || !this.isReady || this.error) return
      const renderId = ++this.renderId
      await this.destroyBook()
      if (this.destroyed || renderId !== this.renderId) return
      this.lightweight = this.forceLightweight || window.innerWidth < 768
      this.readerMode = this.lightweight
      this.pages = this.images.map(page => ({
        id: page.id,
        src: proxy((page.image_variants || {})[this.lightweight ? 'mobile' : 'desktop'] || page.image),
        thumb: proxy((page.image_variants || {}).thumb || page.image)
      }))
      this.currentPage = Math.min(this.currentPage, Math.max(0, this.pages.length - 1))
      await this.$nextTick()
      if (this.destroyed || renderId !== this.renderId) return
      if (this.lightweight) {
        this.measureReader()
        if (this.$refs.pages) this.$refs.pages.scrollLeft = this.$refs.pages.clientWidth * this.currentPage
        return
      }
      try {
        if (typeof window.FlipBook !== 'function') throw new Error('Reader unavailable')
        this.flipbook = new window.FlipBook(this.$el.querySelector('#book-container'), {
          pages: this.pages,
          backgroundTransparent: true,
          backgroundColor: 'transparent',
          startPage: this.currentPage,
          loadAllPages: false,
          loadPagesF: 1,
          loadPagesB: 1,
          pagesInMemory: 6,
          mobile: { pagesInMemory: 3, viewMode: 'swipe' },
          btnSound: { enabled: false }
        })
      } catch (e) {
        this.forceLightweight = true
        await this.setupReader()
      }
    },
    onScroll () {
      if (this.mouseDrag) return
      const element = this.$refs.pages
      if (!element || !element.clientWidth || element.clientWidth !== this.readingWidth) return
      const index = Math.max(0, Math.min(this.pages.length - 1, Math.round(element.scrollLeft / element.clientWidth)))
      if (this.currentPage !== index) { this.currentPage = index; this.zoom = 1 }
    },
    go (index) {
      this.zoom = 1
      this.$refs.pages.scrollTo({ left: Math.max(0, Math.min(this.pages.length - 1, index)) * this.$refs.pages.clientWidth, behavior: Math.abs(index - this.currentPage) > 1 ? 'auto' : 'smooth' })
    },
    imageFailed () { this.error = '圖片載入失敗，請重新載入。' }
  }
}
</script>

<style scoped>
.monthly-reader{touch-action:manipulation;height:100vh;height:100dvh;background:rgba(0,0,0,.72);color:#fff;overflow:hidden;position:fixed;inset:0;z-index:10000;padding-top:44px}
.monthly-message{padding:48px 20px;text-align:center}.monthly-book{height:100%;min-height:0}
.monthly-pages{touch-action:manipulation;overscroll-behavior:contain;display:flex;width:100%;height:calc(100% - 56px - env(safe-area-inset-bottom,0px));overflow-x:auto;scroll-snap-type:x mandatory}
.monthly-pages.is-dragging{scroll-snap-type:none;cursor:grabbing}.monthly-pages{cursor:grab;user-select:none;-webkit-user-select:none}
.monthly-page{flex:0 0 100%;min-width:0;height:100%;scroll-snap-align:start;display:flex;align-items:center;justify-content:center}
.monthly-image-scroll{touch-action:manipulation;overscroll-behavior:contain;display:flex;flex-direction:column;width:100%;height:100%;overflow:auto}.monthly-image-scroll img{display:block;flex:none;min-height:1px;height:auto;margin:auto}
.monthly-message button{background:#fff;color:#222;border:0;border-radius:6px;min-width:38px;min-height:38px}
/* Keep the page strip and zoomed-image scrollbars discreet above the toolbar. */
.monthly-pages,.monthly-image-scroll{scrollbar-width:thin;scrollbar-color:#999 transparent}
.monthly-pages::-webkit-scrollbar,.monthly-image-scroll::-webkit-scrollbar{height:4px;width:4px}
.monthly-pages::-webkit-scrollbar-track,.monthly-image-scroll::-webkit-scrollbar-track{background:transparent}
.monthly-pages::-webkit-scrollbar-thumb,.monthly-image-scroll::-webkit-scrollbar-thumb{background:#999;border-radius:4px}
.monthly-pages::-webkit-scrollbar-corner,.monthly-image-scroll::-webkit-scrollbar-corner{background:transparent}
</style>

<style>
.monthly-close{position:absolute;right:12px;top:4px;z-index:10001;width:36px;height:36px;border:0;border-radius:50%;background:rgba(0,0,0,.4);color:white;font-size:28px;line-height:1;cursor:pointer}
.monthly-reader:fullscreen{background:#333;padding-top:0}
.monthly-reader:-webkit-full-screen{background:#333;padding-top:0}
.monthly-reader:fullscreen .monthly-close{display:none}
.monthly-reader:-webkit-full-screen .monthly-close{display:none}
.monthly-book:fullscreen{background:#333!important}
.monthly-book:-webkit-full-screen{background:#333!important}
</style>
